import React, { useState, useEffect, useRef } from "react";
import { MapCollection, MapItem } from "../types";
import {
    FaArrowLeft,
    FaEye,
    FaEyeSlash,
    FaChartLine,
    FaTags,
    FaStickyNote,
    FaExternalLinkAlt,
    FaCheck,
    FaTimes,
} from "react-icons/fa";
import "../styles_1/FlashcardGenerator.css"; // Reuse generator base styles

interface MapViewerProps {
    session: MapCollection;
    onBack: () => void;
    onUpdate: (updatedSession: MapCollection) => void;
}

export const MapViewer: React.FC<MapViewerProps> = ({
    session,
    onBack,
    onUpdate,
}) => {
    const [showNames, setShowNames] = useState(true);
    const [showTags, setShowTags] = useState(true);
    const [showNotes, setShowNotes] = useState(true);
    const [showDrawings, setShowDrawings] = useState(true);
    const [activePointId, setActivePointId] = useState<string | null>(null);
    const [stickyPointId, setStickyPointId] = useState<string | null>(null);
    const [marks, setMarks] = useState<Record<string, "correct" | "incorrect" | null>>({});
    const [scoringMode, setScoringMode] = useState(false);
    const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
    const [initialLoad, setInitialLoad] = useState(true);

    // Pan & Zoom State
    const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const viewportRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    // SVG Paths for backgrounds (Mirroring atlas-data.js REGIONS)
    const regionSVGs: Record<string, string> = {
        India: "/atlas/maps/india_centered_padded.svg",
        World: "/atlas/maps/BlankMap-World.svg",
        Asia: "/atlas/maps/BLANK_in_Asia.svg",
        Africa: "/atlas/maps/Africa_map_no_countries.svg",
        Europe: "/atlas/maps/Blank_map_of_Europe_(with_disputed_regions).svg",
        NorthAmerica: "/atlas/maps/north-america.svg",
        SouthAmerica: "/atlas/maps/south-america.svg",
        Australia: "/atlas/maps/Australia_location_map.svg"
    };

    const currentSVG = regionSVGs[session.region] || session.region;

    // Handle Pan & Zoom
    useEffect(() => {
        const vp = viewportRef.current;
        if (!vp) return;

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            const delta = -e.deltaY * 0.001 * transform.scale;
            const newScale = Math.max(0.1, Math.min(20, transform.scale + delta));

            // Zoom relative to cursor
            const rect = vp.getBoundingClientRect();
            const cx = e.clientX - rect.left;
            const cy = e.clientY - rect.top;

            const zoomFactor = newScale / transform.scale;
            setTransform(prev => ({
                scale: newScale,
                x: cx - (cx - prev.x) * zoomFactor,
                y: cy - (cy - prev.y) * zoomFactor
            }));
        };

        vp.addEventListener('wheel', handleWheel, { passive: false });
        // Use a standard event listener for pointer pan instead of React ones for better performance
        return () => vp.removeEventListener('wheel', handleWheel);
    }, [transform]);

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX - transform.x, y: e.clientY - transform.y });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        setTransform(prev => ({
            ...prev,
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        }));
    };

    const handleMouseUp = () => setIsDragging(false);

    // Canvas Rendering for Drawings
    useEffect(() => {
        const canvas = canvasRef.current;
        const img = imgRef.current;
        if (!canvas || !img || !showDrawings) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size to match image natural size
        canvas.width = img.naturalWidth || 800;
        canvas.height = img.naturalHeight || 600;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const drawings = session.notebookData.drawings || [];

        const drawStrokeSegment = (p1: any, p2: any, stroke: any) => {
            ctx.save();
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.lineWidth = stroke.size;
            ctx.strokeStyle = stroke.color;
            ctx.globalAlpha = 1.0;

            if (stroke.type === "erase") {
                ctx.globalCompositeOperation = "destination-out";
            } else {
                ctx.globalCompositeOperation = "source-over";
            }

            if (stroke.type === "shade") {
                switch (stroke.shadeType) {
                    case "hb": ctx.globalAlpha = 0.15; ctx.lineWidth = stroke.size * 0.8; break;
                    case "2b": ctx.globalAlpha = 0.35; ctx.lineWidth = stroke.size; break;
                    case "6b": ctx.globalAlpha = 0.65; ctx.lineWidth = stroke.size * 1.4; break;
                    case "charcoal":
                        ctx.globalAlpha = 0.2;
                        ctx.shadowBlur = stroke.size * 2;
                        ctx.shadowColor = stroke.color;
                        ctx.lineWidth = stroke.size * 2;
                        break;
                }
            }

            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
            ctx.restore();
        };

        drawings.forEach((stroke: any) => {
            for (let i = 0; i < stroke.points.length - 1; i++) {
                drawStrokeSegment(stroke.points[i], stroke.points[i + 1], stroke);
            }
        });
    }, [session.notebookData.drawings, showDrawings, session.region]);

    const handlePointClick = (id: string) => {
        if (stickyPointId === id) setStickyPointId(null);
        else setStickyPointId(id);
    };

    const processSVG = (svg: string) => {
        return svg.replace(/fill="[^"]*"/g, 'fill="currentColor"').replace(/<svg/, '<svg style="fill:currentColor"');
    };

    const handleMark = (id: string, status: "correct" | "incorrect") => {
        setMarks((prev) => ({ ...prev, [id]: status }));
    };

    const getScore = () => {
        const correctCount = Object.values(marks).filter(m => m === "correct").length;
        return {
            correct: correctCount,
            total: session.notebookData.points.length,
            percentage: Math.round((correctCount / session.notebookData.points.length) * 100) || 0,
        };
    };

    const handleSubmit = () => {
        const { correct, total, percentage } = getScore();
        const updatedSession = {
            ...session,
            lastAttempt: {
                timestamp: new Date().toISOString(),
                score: correct,
                total: total,
                percentage: percentage,
            }
        };
        onUpdate(updatedSession);
        alert(`🗺️ Map Revision Saved!\n\nScore: ${percentage}% (${correct}/${total})`);
    };

    // Helper to process symbols (similar to app.js)
    const renderSymbol = (p: MapItem) => {
        const scales = session.notebookData.scales || { pin: 1, text: 1, info: 1 };
        const pinScale = (p.scale || 1) * scales.pin;

        const baseStyle: React.CSSProperties = {
            transform: `scale(${pinScale})`,
            transition: 'transform 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        };

        if (p.symbol.startsWith("<svg")) {
            const processed = processSVG(p.symbol);
            return <div dangerouslySetInnerHTML={{ __html: processed }} style={{ ...baseStyle, color: p.color, width: '24px', height: '24px' }} />;
        }
        if (p.symbol === 'triangle') {
            return (
                <div style={{
                    ...baseStyle,
                    width: 0, height: 0,
                    borderLeft: '10px solid transparent',
                    borderRight: '10px solid transparent',
                    borderBottom: `20px solid ${p.color}`
                }} />
            );
        }
        if (p.symbol === 'star') {
            return <div style={{ ...baseStyle, color: p.color, fontSize: '24px' }}>★</div>;
        }
        if (p.symbol === 'square') {
            return <div style={{ ...baseStyle, width: '20px', height: '20px', background: p.color, borderRadius: '2px' }} />;
        }
        // Default dot
        return <div style={{ ...baseStyle, width: '15px', height: '15px', background: p.color, borderRadius: '50%', border: '2px solid white', boxShadow: '0 0 5px rgba(0,0,0,0.2)' }} />;
    };

    return (
        <div className="generator" style={{ maxWidth: "1200px", margin: "0 auto", paddingBottom: "100px", height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* HEADER */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", padding: "10px" }}>
                <button onClick={onBack} className="secondary-btn" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FaArrowLeft /> Back
                </button>
                <div style={{ textAlign: "center" }}>
                    <h1 style={{ margin: 0, color: "#1e293b", fontSize: "1.5rem" }}>{session.name}</h1>
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{session.region} • {session.notebook}</span>
                </div>
                <div style={{ width: '80px' }}></div>
            </div>

            {/* SCORE BANNER (NEW) */}
            {session.lastAttempt && (
                <div style={{
                    background: "#f0fdf4",
                    border: "1px solid #10b981",
                    borderRadius: "12px",
                    padding: "15px",
                    marginBottom: "20px",
                    textAlign: "center",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "15px"
                }}>
                    <span style={{ fontSize: "1.2rem", fontWeight: 800, color: "#15803d" }}>
                        Last Score: {session.lastAttempt.percentage}%
                    </span>
                    <span style={{ color: "#16a34a", fontSize: "0.9rem" }}>
                        ({session.lastAttempt.score} / {session.lastAttempt.total} Pins identified)
                    </span>
                </div>
            )}

            {/* CONTROLS */}
            <div style={{ background: "white", padding: "15px", borderRadius: "12px", marginBottom: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", display: "flex", gap: "15px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: "10px" }}>
                    <button onClick={() => setShowNames(!showNames)} className={`secondary-btn ${showNames ? 'active' : ''}`} style={{ fontSize: '0.8rem' }}>
                        {showNames ? <FaEye /> : <FaEyeSlash />} Names
                    </button>
                    <button onClick={() => setShowTags(!showTags)} className={`secondary-btn ${showTags ? 'active' : ''}`} style={{ fontSize: '0.8rem' }}>
                        {showTags ? <FaTags /> : <FaEyeSlash />} Tags
                    </button>
                    <button onClick={() => setShowNotes(!showNotes)} className={`secondary-btn ${showNotes ? 'active' : ''}`} style={{ fontSize: '0.8rem' }}>
                        {showNotes ? <FaStickyNote /> : <FaEyeSlash />} Notes
                    </button>
                    <button onClick={() => setShowDrawings(!showDrawings)} className={`secondary-btn ${showDrawings ? 'active' : ''}`} style={{ fontSize: '0.8rem' }}>
                        {showDrawings ? <FaChartLine /> : <FaEyeSlash />} Drawings
                    </button>
                    <button onClick={() => setScoringMode(!scoringMode)} className={`secondary-btn ${scoringMode ? 'active' : ''}`} style={{ fontSize: '0.8rem', color: scoringMode ? '#f97316' : '#64748b' }}>
                        <FaChartLine /> {scoringMode ? "Exit Revision" : "Start Revision"}
                    </button>
                </div>

                {scoringMode && (
                    <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                        <span style={{ fontWeight: 700, fontSize: "1rem", color: "#334155" }}>
                            Score: {getScore().correct} / {session.notebookData.points.length}
                        </span>
                        <button onClick={handleSubmit} className="primary-btn" style={{ padding: "8px 16px", background: "#10b981", fontSize: '0.85rem' }}>
                            Save Score
                        </button>
                    </div>
                )}
            </div>

            {/* MAP AREA */}
            <div
                ref={viewportRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{
                    flex: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    background: '#f8fafc',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    cursor: isDragging ? 'grabbing' : 'grab'
                }}
            >
                <div style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                    transformOrigin: '0 0'
                }}>
                    {/* BACKGROUND IMAGE */}
                    <img
                        ref={imgRef}
                        src={currentSVG}
                        alt="Map"
                        style={{ display: 'block', width: 'auto', height: 'auto', pointerEvents: 'none', userSelect: 'none' }}
                        onLoad={(e) => {
                            const img = e.currentTarget;
                            const nw = img.naturalWidth;
                            const nh = img.naturalHeight;
                            setImgSize({ width: nw, height: nh });

                            // Initial centering
                            if (initialLoad && viewportRef.current) {
                                const vrect = viewportRef.current.getBoundingClientRect();
                                // Calculate scale to fit at least 80% of viewport
                                const s = Math.min(vrect.width / nw, vrect.height / nh) * 0.9;
                                setTransform({
                                    x: (vrect.width - nw * s) / 2,
                                    y: (vrect.height - nh * s) / 2,
                                    scale: s
                                });
                                setInitialLoad(false);
                            }
                            // Re-render drawings
                            setShowDrawings(prev => prev);
                        }}
                    />

                    {/* CONTENT CONTAINER (Matches Image Size) */}
                    <div style={{
                        position: 'absolute',
                        top: 0, left: 0,
                        width: imgSize.width || '100%',
                        height: imgSize.height || '100%',
                        pointerEvents: 'none'
                    }}>
                        {/* DRAWING CANVAS */}
                        {showDrawings && (
                            <canvas
                                ref={canvasRef}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    pointerEvents: 'none'
                                }}
                            />
                        )}

                        {/* PINS & TEXT BOXES */}
                        {session.notebookData.points.map((p) => {
                            const isSticky = stickyPointId === p.id;
                            const isHovered = activePointId === p.id;
                            const showInfo = isSticky || isHovered;
                            const markStatus = marks[p.id];
                            const scales = session.notebookData.scales || { pin: 1, text: 1, info: 1 };

                            const left = `${p.x}%`;
                            const top = `${p.y}%`;

                            if (p.type === "text") {
                                return (
                                    <div
                                        key={p.id}
                                        style={{
                                            position: 'absolute',
                                            left, top,
                                            width: p.width ? `${(p.width / imgSize.width) * 100}%` : 'auto',
                                            height: p.height ? `${(p.height / imgSize.height) * 100}%` : 'auto',
                                            color: p.color,
                                            fontSize: `${14 * scales.text}px`,
                                            fontWeight: 600,
                                            pointerEvents: 'auto',
                                            display: showNames ? 'block' : 'none',
                                            transform: `scale(${p.scale || 1})`,
                                            transformOrigin: 'top left'
                                        }}
                                    >
                                        {p.label}
                                    </div>
                                );
                            }
                            return (
                                <div
                                    key={p.id}
                                    style={{
                                        position: 'absolute', left, top,
                                        transform: 'translate(-50%, -50%)',
                                        zIndex: showInfo ? 100 : 10,
                                        pointerEvents: 'auto'
                                    }}
                                    onMouseEnter={() => setActivePointId(p.id)}
                                    onMouseLeave={() => setActivePointId(null)}
                                    onClick={() => handlePointClick(p.id)}
                                >
                                    {/* Pin Shape */}
                                    <div style={{ cursor: 'pointer', transition: 'transform 0.2s' }}>
                                        {renderSymbol(p)}
                                    </div>

                                    {/* Info Card */}
                                    {showInfo && (
                                        <div style={{
                                            position: 'absolute', top: '100%', left: '50%',
                                            transform: `translateX(-50%) scale(${scales.info * (p.infoScale || 1)})`,
                                            transformOrigin: 'top center',
                                            background: 'white', padding: '12px', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                                            width: '200px', marginTop: '10px', pointerEvents: isSticky ? 'auto' : 'none'
                                        }}>
                                            {showNames && <h4 style={{ margin: '0 0 5px 0', fontSize: '0.9rem', color: '#1e293b' }}>{p.label}</h4>}
                                            {showTags && p.tag && (
                                                <span style={{ fontSize: '0.7rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', color: '#64748b' }}>
                                                    {p.tag}
                                                </span>
                                            )}
                                            {showNotes && p.notes && (
                                                <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: '#475569', lineHeight: '1.4' }}>{p.notes}</p>
                                            )}

                                            {p.url && isSticky && (
                                                <a href={p.url} target="_blank" rel="noopener noreferrer" style={{
                                                    display: 'flex', alignItems: 'center', gap: '5px', marginTop: '10px', fontSize: '0.75rem', color: '#3b82f6', textDecoration: 'none', fontWeight: 600
                                                }}>
                                                    <FaExternalLinkAlt size={10} /> Research Link
                                                </a>
                                            )}

                                            {scoringMode && isSticky && (
                                                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', borderTop: '1px solid #f1f5f9', paddingTop: '10px' }}>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleMark(p.id, 'correct'); }}
                                                        style={{ flex: 1, padding: '5px', borderRadius: '4px', border: 'none', background: markStatus === 'correct' ? '#22c55e' : '#f1f5f9', color: markStatus === 'correct' ? 'white' : '#22c55e', cursor: 'pointer' }}
                                                    >
                                                        <FaCheck />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleMark(p.id, 'incorrect'); }}
                                                        style={{ flex: 1, padding: '5px', borderRadius: '4px', border: 'none', background: markStatus === 'incorrect' ? '#ef4444' : '#f1f5f9', color: markStatus === 'incorrect' ? 'white' : '#ef4444', cursor: 'pointer' }}
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* FOOTER LEGEND */}
            <div style={{ padding: '15px', color: '#64748b', fontSize: '0.8rem', textAlign: 'center' }}>
                Tip: Click a pin to make the info card stick. Use Revision mode to test yourself!
            </div>
        </div>
    );
};
